/**
 * Bookkeeping for heavy work the page does in the background: a scene warming up offscreen,
 * its code and data being prepared after the intro.
 *
 * That work can briefly slow the scene on screen. The frame-rate monitor in `SceneRenderer`
 * asks {@link recentBackgroundWork} before it lowers resolution or quality, so a dip the page
 * caused itself never costs the visible scene (the hero orb, usually) its sharpness for the
 * rest of the visit.
 *
 * @module
 */

/** How long after background work ends its dips still count as ours, in ms. A monitor
 * decision covers the last 2.5 s of frames (10 samples of 250 ms). */
export const BACKGROUND_SETTLE_MS = 3000

let active = 0
let lastEnded = Number.NEGATIVE_INFINITY

/** Marks heavy background work as started. Call the returned function once it ends. */
export function beginBackgroundWork(): () => void {
  active++
  let ended = false
  return () => {
    if (ended) return
    ended = true
    active = Math.max(0, active - 1)
    lastEnded = performance.now()
  }
}

/** True while background work runs, and for {@link BACKGROUND_SETTLE_MS} after it ends. */
export function recentBackgroundWork(now = performance.now()): boolean {
  return active > 0 || now - lastEnded < BACKGROUND_SETTLE_MS
}
